import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  captureLead,
  createCheckoutSession,
  fetchPrices,
  trackLeadServerEvent,
  trackPageViewServerEvent,
  trackViewContentServerEvent,
  type StripePricesResponse,
} from "./api";
import { setCheckoutCompletionCookie } from "./checkoutCookie";
import {
  captureFunnelScreenPageview,
  firePageViewBrowser,
  getTrackingContext,
  initAnalytics,
  registerFunnelPersonProperties,
  rememberPendingCheckoutTracking,
  track,
  trackMetaInitiateCheckout,
  trackMetaLead,
  trackMetaViewContent,
  upgradeExternalIdToLead,
  waitForFbp,
} from "./analytics";
import { brand, config } from "./config";
import { FunnelDemo, HeroVisual } from "./heroVisuals";
import { getFeaturedExplainerIndex, getPickedAgentChips, listPickedAgents, getQuestionsForConcept } from "./funnelData";
import {
  checkoutAfterPaymentSteps,
  checkoutPlans,
  getRecommendedPlan,
  getResultInsights,
  moneyBackGuaranteeLabel,
  paymentExplainerSteps,
  paymentFaqs,
  resultBullets,
  resultFeatureOverview,
  socialProof,
  testimonials,
  trialCheckoutPlans,
  type Answers,
  type CheckoutOptionId,
  type CheckoutPlan,
  type PlanId,
} from "./funnelData";
import { BrandLogo, Icon, QuestionIcon } from "./icons";
import { PixelMark } from "./PixelMark";
import { FunnelVideoTestimonials } from "./VideoTestimonials";
import { getCheckoutOfferFromLocation, isPolarTrialOffer, type CheckoutOffer } from "./checkoutOffer";
import {
  DEFAULT_FUNNEL_CONCEPT,
  getDeepMatch,
  getFunnelConceptFromLocation,
  getStoredOrDefaultFunnelConcept,
  type FunnelConcept,
} from "./funnelConcept";
import {
  EMAIL_STEP,
  getFunnelScreenName,
  getHashForScreen,
  getPaymentStep,
  parseHashToScreen,
  RESULT_STEP,
  WELCOME_STEP,
} from "./funnelRoutes";

type StoredFunnelState = {
  step?: number;
  email?: string;
  answers?: Answers;
  selectedOptionId?: CheckoutOptionId;
  selectedPlan?: PlanId;
  leadId?: number | null;
  entryConceptKey?: string;
};

type CheckoutTimelineStep = {
  title: string;
  subtitle: string;
  icon?: "bell";
};

type PricesByOffer = Partial<Record<string, StripePricesResponse>>;

// v3: the quiz gained a persona question (5 steps), so old v2 step indices no longer line up.
const STORAGE_KEY = "agentmax:funnel:v3";
const CHECKOUT_TOKEN_KEY = "agentmax:checkout-completion-token";
const META_VIEW_CONTENT_SESSION_KEY = "agentmax:meta-view-content-fired";
const META_LEAD_SESSION_PREFIX = "agentmax:meta-lead-fired:";
const META_PAGE_VIEW_SESSION_KEY = "agentmax:meta-page-view-fired";
const EMAIL_STEP_QUERY_PARAM = "collect_email";
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MOBILE_MEDIA_QUERY = "(max-width: 760px)";
// Below this width the sticky desktop conversion sidebar is hidden and the fixed
// bottom checkout bar (same email + CTA) takes over. Must match the `.side-proof`
// hide breakpoint in styles.css.
const PAYMENT_FLOATING_CTA_MEDIA_QUERY = "(max-width: 860px)";
const emailFrictionHandlers = ["Pay once", "No subscription", "No spam"] as const;
const emailFrictionHandlersTrial = ["No charge today", "Cancel anytime", "No spam"] as const;

const getInitialEntryConcept = (): FunnelConcept => {
  if (typeof window === "undefined") return DEFAULT_FUNNEL_CONCEPT;
  // A live ?concept= in the URL always wins (matches the ad creative the user just clicked).
  const fromUrl = getFunnelConceptFromLocation(window.location);
  if (fromUrl) return fromUrl;
  // Otherwise keep the ad-matched title from a resumed session.
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const storedKey = raw ? (JSON.parse(raw) as StoredFunnelState).entryConceptKey : null;
    return getStoredOrDefaultFunnelConcept(storedKey);
  } catch {
    return DEFAULT_FUNNEL_CONCEPT;
  }
};

const money = (unitAmount: number, currency: string): string =>
  new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: currency.toUpperCase(),
    maximumFractionDigits: unitAmount % 100 === 0 ? 0 : 2,
  }).format(unitAmount / 100);

const annualMoney = (price: { unitAmount: number; currency: string } | undefined, fallback: string): string =>
  price ? `${money(price.unitAmount, price.currency)}/year` : fallback;

const fallbackCurrencyForPrice = (price: string): string => {
  const trimmed = price.trim().toUpperCase();
  if (trimmed.startsWith("A$") || trimmed.startsWith("AU$") || trimmed.startsWith("AUD")) return "AUD";
  if (trimmed.startsWith("£") || trimmed.startsWith("GBP")) return "GBP";
  if (trimmed.startsWith("€") || trimmed.startsWith("EUR")) return "EUR";
  return "USD";
};

const fallbackUnitAmountForPrice = (price: string): number => {
  const parsed = Number(price.replace(/[^0-9.]/g, ""));
  return Number.isFinite(parsed) ? Math.round(parsed * 100) : 0;
};

const monthlyMoney = (price: { unitAmount: number; currency: string } | undefined, fallback: string): string => {
  const unitAmount = price?.unitAmount ?? fallbackUnitAmountForPrice(fallback);
  const currency = price?.currency ?? fallbackCurrencyForPrice(fallback);
  return money(Math.floor(unitAmount / 12), currency);
};

const normalizeEmail = (value: string): string => value.trim().toLowerCase();
const isValidEmail = (value: string): boolean => EMAIL_REGEX.test(value);
const RESERVED_EMAIL_DOMAINS = new Set(["example.com", "example.net", "example.org", "invalid", "localhost", "test.com"]);
const isReservedEmailDomain = (value: string): boolean => {
  const domain = value.split("@").pop()?.trim().toLowerCase() ?? "";
  return RESERVED_EMAIL_DOMAINS.has(domain);
};
const checkoutEmailError = (value: string): string | null => {
  if (!isValidEmail(value)) return "Enter a valid email for download and activation details.";
  if (isReservedEmailDomain(value)) return "Use a real inbox that can receive checkout and activation emails.";
  return null;
};

const parseBooleanQueryFlag = (location: Location, queryParam: string, fallback: boolean): boolean => {
  const raw = location.search ? new URLSearchParams(location.search).get(queryParam)?.trim().toLowerCase() : null;
  if (raw == null || raw === "") return fallback;
  if (raw === "1" || raw === "true" || raw === "yes" || raw === "on") return true;
  if (raw === "0" || raw === "false" || raw === "no" || raw === "off") return false;
  return fallback;
};

// The dedicated email step is on by default so every ad click-through captures the lead
// before checkout. Append `?collect_email=0` to any funnel URL to fall back to the inline
// email-at-payment flow (useful for A/B or debugging).
const shouldShowEmailStepFromLocation = (location: Location): boolean =>
  parseBooleanQueryFlag(location, EMAIL_STEP_QUERY_PARAM, true);

const allCheckoutPlans = [...checkoutPlans, ...trialCheckoutPlans];
const checkoutOptionIds = new Set<CheckoutOptionId>(allCheckoutPlans.map((plan) => plan.id));

const isCheckoutOptionId = (value: unknown): value is CheckoutOptionId =>
  typeof value === "string" && checkoutOptionIds.has(value as CheckoutOptionId);

const getCheckoutOptions = (isTrialCheckoutOffer: boolean): CheckoutPlan[] =>
  isTrialCheckoutOffer ? trialCheckoutPlans : checkoutPlans;

const recommendedOptionIdForPlan = (
  planId: Exclude<PlanId, "unlimited">,
  isTrialCheckoutOffer: boolean,
): CheckoutOptionId => {
  if (!isTrialCheckoutOffer) return planId;
  return planId === "standard" ? "standard_trial" : "pro_trial";
};

const findCheckoutPlan = (optionId: CheckoutOptionId, options: CheckoutPlan[] = allCheckoutPlans): CheckoutPlan =>
  options.find((plan) => plan.id === optionId) ?? options[0] ?? allCheckoutPlans[0];

const getInitialCheckoutOffer = (): CheckoutOffer => {
  if (typeof window === "undefined") return getCheckoutOfferFromLocation({ search: "" } as Location);
  return getCheckoutOfferFromLocation(window.location);
};

// Land straight on the first question — no welcome splash. Question-first converts
// better (fewer clicks, momentum) and keeps the tap targets above the mobile fold.
const LANDING_STEP = WELCOME_STEP + 1;

const getInitialStep = (): number => {
  if (typeof window === "undefined") return LANDING_STEP;
  const includeEmailStep = shouldShowEmailStepFromLocation(window.location);
  return parseHashToScreen(window.location.hash, { includeEmailStep })?.step ?? LANDING_STEP;
};

function TrustBadge({ className }: { className?: string }) {
  return (
    <div
      className={className ? `trust-badge ${className}` : "trust-badge"}
      aria-label={`Rated ${socialProof.rating} out of ${socialProof.ratingMax}, trusted by ${socialProof.audience}`}
    >
      <div className="trust-badge__avatars" aria-hidden="true">
        {socialProof.avatars.map((avatar) => (
          <img key={avatar} src={avatar} alt="" loading="lazy" decoding="async" width={32} height={32} />
        ))}
      </div>
      <div className="trust-badge__copy">
        <span className="trust-badge__stars" aria-hidden="true">
          ★★★★★ <strong>{socialProof.rating}/{socialProof.ratingMax}</strong>
        </span>
        <span className="trust-badge__label">Trusted by {socialProof.audience}</span>
      </div>
    </div>
  );
}

export default function App() {
  const [entryConcept] = useState<FunnelConcept>(() => getInitialEntryConcept());
  const [checkoutOffer] = useState<CheckoutOffer>(() => getInitialCheckoutOffer());
  const [showDedicatedEmailStep] = useState<boolean>(() =>
    typeof window !== "undefined" ? shouldShowEmailStepFromLocation(window.location) : false,
  );
  const [step, setStep] = useState(() => getInitialStep());
  const [answers, setAnswers] = useState<Answers>({});
  const [email, setEmail] = useState("");
  const [leadId, setLeadId] = useState<number | null>(null);
  const [selectedOptionId, setSelectedOptionId] = useState<CheckoutOptionId>("pro");
  const [pricesByOffer, setPricesByOffer] = useState<PricesByOffer>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [expandedMobileOptionId, setExpandedMobileOptionId] = useState<CheckoutOptionId>("pro");
  const [isMobile, setIsMobile] = useState(false);
  const [showFloatingCheckoutCta, setShowFloatingCheckoutCta] = useState(false);
  const lastCapturedLeadEmailRef = useRef<string | null>(null);
  const emailCaptureInFlightRef = useRef<string | null>(null);

  const deepMatch = useMemo(() => getDeepMatch(entryConcept), [entryConcept]);
  // Loop is a cloud concept: drop local-first / on-your-Mac claims that would contradict it.
  const cloudConcept = Boolean(deepMatch?.cloudConcept);
  // The concept's ad promise picks the active quiz (persona for all, plus a swapped demand
  // question for the voice/loop concepts). Same length as the base quiz, so step math holds.
  const activeQuestions = useMemo(
    () => getQuestionsForConcept(deepMatch?.quizSwapQuestionId, deepMatch?.quizSubtitles),
    [deepMatch],
  );
  const routeOptions = useMemo(() => ({ includeEmailStep: showDedicatedEmailStep }), [showDedicatedEmailStep]);
  const paymentStep = getPaymentStep(showDedicatedEmailStep);
  const showWelcome = step === WELCOME_STEP;
  const currentQuestion = step >= 1 && step <= activeQuestions.length ? activeQuestions[step - 1] : undefined;
  const showQuestion = Boolean(currentQuestion);
  const showResult = step === RESULT_STEP;
  const showEmail = showDedicatedEmailStep && step === EMAIL_STEP;
  const showPayment = step >= paymentStep;
  const progress = Math.min(100, Math.round((step / paymentStep) * 100));
  const recommendedPlan = useMemo(() => getRecommendedPlan(answers), [answers]);
  const resultInsights = useMemo(() => getResultInsights(answers), [answers]);
  const pageUrl = typeof window === "undefined" ? undefined : window.location.href;
  const normalizedEmail = normalizeEmail(email);
  const hasValidCheckoutEmail = isValidEmail(normalizedEmail);
  const canGoBack = step > LANDING_STEP;
  const isLandingScreen = step === LANDING_STEP;
  const isTrialCheckoutOffer = isPolarTrialOffer(checkoutOffer);
  const offerAnalyticsKey = checkoutOffer.analyticsKey;
  const activeCheckoutPlans = useMemo(() => getCheckoutOptions(isTrialCheckoutOffer), [isTrialCheckoutOffer]);
  const selectedPlanData = findCheckoutPlan(selectedOptionId, activeCheckoutPlans);
  const selectedIsAnnualTrial = selectedPlanData.billingMode === "annual_trial";
  const selectedPlan = selectedPlanData.planId;
  const priceForOption = useCallback(
    (option: CheckoutPlan) => pricesByOffer[option.offer]?.[option.planId],
    [pricesByOffer],
  );

  const normalizeStepForFlow = useCallback(
    (candidateStep: number): number => {
      const nextStep = Number.isFinite(candidateStep) ? Math.max(WELCOME_STEP, candidateStep) : WELCOME_STEP;
      if (showDedicatedEmailStep) return nextStep;
      return nextStep >= EMAIL_STEP ? paymentStep : nextStep;
    },
    [paymentStep, showDedicatedEmailStep],
  );

  useEffect(() => {
    initAnalytics();
    track("funnel_loaded", {
      entry_concept: entryConcept.key,
      entry_concept_label: entryConcept.label,
      checkout_offer: offerAnalyticsKey,
    });
  }, [entryConcept.key, entryConcept.label, offerAnalyticsKey]);

  useEffect(() => {
    if (typeof sessionStorage === "undefined") return;
    if (sessionStorage.getItem(META_PAGE_VIEW_SESSION_KEY) === "1") return;
    sessionStorage.setItem(META_PAGE_VIEW_SESSION_KEY, "1");
    const tracking = getTrackingContext();
    const pageViewEventId = `pv-${tracking.eventId}`;
    firePageViewBrowser(pageViewEventId);
    if (!config.apiBaseUrl) return;
    void waitForFbp().then(() => {
      const fresh = getTrackingContext();
      void trackPageViewServerEvent({
        eventId: pageViewEventId,
        fbp: fresh.fbp,
        fbc: fresh.fbc,
        externalId: fresh.externalId,
        pageUrl,
      }).catch((e) => console.warn("[meta] PageView CAPI failed", e));
    });
  }, [pageUrl]);

  useEffect(() => {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      try {
        const parsed = JSON.parse(raw) as StoredFunnelState;
        setStep(typeof parsed.step === "number" ? Math.max(LANDING_STEP, normalizeStepForFlow(parsed.step)) : LANDING_STEP);
        setEmail(parsed.email ?? "");
        setAnswers(parsed.answers ?? {});
        setLeadId(parsed.leadId ?? null);
        if (isCheckoutOptionId(parsed.selectedOptionId) && activeCheckoutPlans.some((plan) => plan.id === parsed.selectedOptionId)) {
          setSelectedOptionId(parsed.selectedOptionId);
        } else if (parsed.selectedPlan === "standard" || parsed.selectedPlan === "pro") {
          setSelectedOptionId(recommendedOptionIdForPlan(parsed.selectedPlan, isTrialCheckoutOffer));
        }
      } catch {
        localStorage.removeItem(STORAGE_KEY);
      }
    }

    const hashScreen = parseHashToScreen(window.location.hash, routeOptions);
    if (hashScreen) setStep(Math.max(LANDING_STEP, normalizeStepForFlow(hashScreen.step)));
  }, [activeCheckoutPlans, isTrialCheckoutOffer, normalizeStepForFlow, routeOptions]);

  useEffect(() => {
    if (activeCheckoutPlans.some((plan) => plan.id === selectedOptionId)) return;
    setSelectedOptionId(isTrialCheckoutOffer ? "pro_trial" : "pro");
  }, [activeCheckoutPlans, isTrialCheckoutOffer, selectedOptionId]);

  useEffect(() => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        step,
        email,
        answers,
        selectedOptionId,
        selectedPlan,
        leadId,
        entryConceptKey: entryConcept.key,
      }),
    );
  }, [step, email, answers, selectedOptionId, selectedPlan, leadId, entryConcept.key]);

  useEffect(() => {
    if (!config.apiBaseUrl) return;
    const offers = Array.from(new Set(activeCheckoutPlans.map((plan) => plan.offer)));
    let canceled = false;

    void Promise.all(
      offers.map(async (offer) => {
        const prices = await fetchPrices(offer);
        return [offer, prices] as const;
      }),
    )
      .then((entries) => {
        if (!canceled) setPricesByOffer(Object.fromEntries(entries));
      })
      .catch(() => {
        if (!canceled) setPricesByOffer({});
      });

    return () => {
      canceled = true;
    };
  }, [activeCheckoutPlans]);

  // Keep the mobile-expanded plan in sync with the chosen plan.
  useEffect(() => {
    setExpandedMobileOptionId(selectedOptionId);
  }, [selectedOptionId]);

  // Track the mobile breakpoint so the floating CTA + collapsed cards only apply on small screens.
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const media = window.matchMedia(MOBILE_MEDIA_QUERY);
    const sync = () => setIsMobile(media.matches);
    sync();
    media.addEventListener("change", sync);
    return () => media.removeEventListener("change", sync);
  }, []);

  // On the payment step, small screens get a fixed bottom checkout bar (email + CTA);
  // the sticky desktop sidebar carries the same controls above the breakpoint.
  useEffect(() => {
    if (!showPayment) {
      setShowFloatingCheckoutCta(false);
      return;
    }
    if (typeof window === "undefined" || !window.matchMedia) return;
    const media = window.matchMedia(PAYMENT_FLOATING_CTA_MEDIA_QUERY);
    const sync = () => setShowFloatingCheckoutCta(media.matches);
    sync();
    media.addEventListener("change", sync);
    return () => media.removeEventListener("change", sync);
  }, [showPayment]);

  useEffect(() => {
    registerFunnelPersonProperties({
      recommended_plan: recommendedPlan,
      entry_concept: entryConcept.key,
      entry_concept_label: entryConcept.label,
      checkout_offer: offerAnalyticsKey,
    });
  }, [recommendedPlan, entryConcept.key, entryConcept.label, offerAnalyticsKey]);

  // Persona is captured for learning who converts per concept, not to branch the funnel.
  useEffect(() => {
    if (answers.persona_level) registerFunnelPersonProperties({ persona_level: answers.persona_level });
  }, [answers.persona_level]);

  useEffect(() => {
    captureFunnelScreenPageview(
      step,
      { recommendedPlan, entryConceptKey: entryConcept.key, entryConceptLabel: entryConcept.label },
      routeOptions,
    );
  }, [step, recommendedPlan, entryConcept.key, entryConcept.label, routeOptions]);

  useEffect(() => {
    track("funnel_step_viewed", {
      step,
      screen_name: getFunnelScreenName(step, routeOptions),
      recommended_plan: recommendedPlan,
      entry_concept: entryConcept.key,
      checkout_offer: offerAnalyticsKey,
    });
  }, [step, recommendedPlan, entryConcept.key, routeOptions, offerAnalyticsKey]);

  useEffect(() => {
    const nextHash = getHashForScreen(step, routeOptions);
    if (window.location.hash !== nextHash) {
      window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}${nextHash}`);
    }
  }, [step, routeOptions]);

  const fireViewContent = useCallback(() => {
    if (typeof sessionStorage !== "undefined" && sessionStorage.getItem(META_VIEW_CONTENT_SESSION_KEY)) return;
    // Guard synchronously so awaiting _fbp below can't let a second call slip through.
    if (typeof sessionStorage !== "undefined") sessionStorage.setItem(META_VIEW_CONTENT_SESSION_KEY, "1");
    const tracking = getTrackingContext();
    const optionId = recommendedOptionIdForPlan(recommendedPlan, isTrialCheckoutOffer);
    const option = findCheckoutPlan(optionId, activeCheckoutPlans);
    const live = priceForOption(option);
    const value = live ? live.unitAmount / 100 : fallbackUnitAmountForPrice(option.price) / 100;
    const currency = (live?.currency ?? fallbackCurrencyForPrice(option.price)).toUpperCase();
    const contentName = `AgentMax ${option.name}`;
    const viewContentEventId = `vc-${tracking.eventId}`;
    trackMetaViewContent({ eventId: viewContentEventId, contentIds: [option.planId], value, currency, contentName });
    // Wait for the _fbp cookie so the server event carries it (browser + server dedup on eventId).
    void waitForFbp()
      .then(() => {
        const fresh = getTrackingContext();
        return trackViewContentServerEvent({
          eventId: viewContentEventId,
          contentIds: [option.planId],
          value,
          currency,
          contentName,
          fbp: fresh.fbp,
          fbc: fresh.fbc,
          externalId: fresh.externalId,
          pageUrl,
        });
      })
      .catch((e) => console.warn("[meta] ViewContent CAPI failed", e));
  }, [activeCheckoutPlans, isTrialCheckoutOffer, pageUrl, priceForOption, recommendedPlan]);

  const advanceFromWelcome = useCallback(() => {
    setStep(1);
  }, []);

  const selectAnswer = useCallback((questionId: string, optionId: string) => {
    setAnswers((current) => ({ ...current, [questionId]: optionId }));
    track("funnel_answer_selected", {
      question_id: questionId,
      option_id: optionId,
      entry_concept: entryConcept.key,
    });
    window.setTimeout(() => {
      setStep((current) => {
        if (current <= activeQuestions.length) return current + 1;
        return current;
      });
    }, 160);
  }, [entryConcept.key, activeQuestions.length]);

  /** Multi-select questions toggle membership in a comma-joined, sorted id list. */
  const toggleAnswer = useCallback((questionId: string, optionId: string) => {
    setAnswers((current) => {
      const selected = new Set((current[questionId] ?? "").split(",").filter(Boolean));
      if (selected.has(optionId)) {
        selected.delete(optionId);
      } else {
        selected.add(optionId);
      }
      return { ...current, [questionId]: [...selected].sort().join(",") };
    });
    track("funnel_answer_toggled", {
      question_id: questionId,
      option_id: optionId,
      entry_concept: entryConcept.key,
    });
  }, [entryConcept.key]);

  const advanceFromQuestion = useCallback(() => {
    setStep((current) => (current <= activeQuestions.length ? current + 1 : current));
  }, [activeQuestions.length]);

  useEffect(() => {
    if (step !== RESULT_STEP) return;
    setSelectedOptionId(recommendedOptionIdForPlan(recommendedPlan, isTrialCheckoutOffer));
    fireViewContent();
  }, [step, recommendedPlan, isTrialCheckoutOffer, fireViewContent]);

  const advanceFromResult = useCallback(() => {
    setError("");
    setStep(showDedicatedEmailStep ? EMAIL_STEP : paymentStep);
  }, [paymentStep, showDedicatedEmailStep]);

  const captureEmailLead = useCallback(
    (rawEmail: string): boolean => {
      const nextEmail = normalizeEmail(rawEmail);
      if (!config.enableEmailCapture || !isValidEmail(nextEmail)) return false;
      if (!config.apiBaseUrl) return false;
      if (lastCapturedLeadEmailRef.current === nextEmail || emailCaptureInFlightRef.current === nextEmail) {
        return true;
      }

      emailCaptureInFlightRef.current = nextEmail;
      const leadTracking = getTrackingContext();
      const leadEventId = `lead-${leadTracking.eventId}`;
      const leadMetaSessionKey = `${META_LEAD_SESSION_PREFIX}${nextEmail}`;
      const shouldTrackLeadMetaEvent =
        typeof sessionStorage === "undefined" || sessionStorage.getItem(leadMetaSessionKey) !== "1";

      void (async () => {
        try {
          const [leadSaveResult, leadTrackResult] = await Promise.allSettled([
            captureLead({
              email: nextEmail,
              onboardingResponses: { concept: entryConcept.key, ...answers },
              source: "agentmax_webfunnel",
            }),
            shouldTrackLeadMetaEvent
              ? trackLeadServerEvent({
                  email: nextEmail,
                  eventId: leadEventId,
                  fbp: leadTracking.fbp,
                  fbc: leadTracking.fbc,
                  externalId: leadTracking.externalId,
                  pageUrl,
                })
              : Promise.resolve(),
          ]);

          if (leadSaveResult.status === "fulfilled") {
            lastCapturedLeadEmailRef.current = nextEmail;
            setLeadId(leadSaveResult.value.leadId);
            if (typeof leadSaveResult.value.leadId === "number") {
              upgradeExternalIdToLead(leadSaveResult.value.leadId);
            }
            track("email_captured", { lead_id: leadSaveResult.value.leadId, recommended_plan: recommendedPlan });
          }

          if (shouldTrackLeadMetaEvent) {
            if (leadTrackResult.status === "rejected") console.warn("[leads] CAPI lead failed", leadTrackResult.reason);
            trackMetaLead(leadEventId, { email: nextEmail });
            sessionStorage.setItem(leadMetaSessionKey, "1");
          }
        } finally {
          if (emailCaptureInFlightRef.current === nextEmail) emailCaptureInFlightRef.current = null;
        }
      })();

      return true;
    },
    [answers, entryConcept.key, pageUrl, recommendedPlan],
  );

  const saveEmailAndContinue = () => {
    setError("");
    const emailError = checkoutEmailError(normalizedEmail);
    if (emailError) {
      setError(emailError);
      return;
    }
    captureEmailLead(normalizedEmail);
    setStep(paymentStep);
  };

  const startCheckout = async (optionId: CheckoutOptionId) => {
    setError("");
    const option = findCheckoutPlan(optionId, activeCheckoutPlans);
    setSelectedOptionId(option.id);
    if (!config.apiBaseUrl) {
      setError("Checkout API is not configured. Set VITE_API_BASE_URL to your agentmax-checkout-api worker URL.");
      return;
    }

    try {
      setIsLoading(true);
      const price = priceForOption(option);
      const tracking = getTrackingContext();
      const emailError = normalizedEmail ? checkoutEmailError(normalizedEmail) : null;
      if (emailError) {
        setError(emailError);
        return;
      }
      if (hasValidCheckoutEmail) captureEmailLead(normalizedEmail);
      const value = price ? price.unitAmount / 100 : fallbackUnitAmountForPrice(option.price) / 100;
      const currency = (price?.currency ?? fallbackCurrencyForPrice(option.price)).toUpperCase();
      track("checkout_started", {
        plan_id: option.planId,
        offer_id: option.id,
        value,
        currency,
        checkout_offer: option.offer,
      });
      trackMetaInitiateCheckout(tracking.eventId, option.planId, value, currency);

      const response = await createCheckoutSession({
        email: hasValidCheckoutEmail ? normalizedEmail : undefined,
        leadId: leadId ?? undefined,
        planId: option.planId,
        flow: "a",
        offer: option.offer,
        value,
        eventId: tracking.eventId,
        fbp: tracking.fbp ?? undefined,
        fbc: tracking.fbc ?? undefined,
        externalId: tracking.externalId,
        pageUrl,
        surface: "quiz",
      });

      track(checkoutOffer.provider === "polar" ? "checkout_redirect_to_polar" : "checkout_redirect_to_stripe", {
        plan_id: option.planId,
        offer_id: option.id,
        checkout_session_id: response.sessionId,
        checkout_offer: option.offer,
        value,
        currency,
      });

      sessionStorage.setItem(CHECKOUT_TOKEN_KEY, response.completionToken);
      setCheckoutCompletionCookie(response.completionToken);
      rememberPendingCheckoutTracking({
        sessionId: response.sessionId,
        eventId: tracking.eventId,
        planId: option.planId,
        value,
        currency,
        completionToken: response.completionToken,
      });
      window.location.assign(response.checkoutUrl);
    } catch (checkoutError) {
      track("checkout_failed", {
        plan_id: option.planId,
        offer_id: option.id,
        checkout_offer: option.offer,
        message: checkoutError instanceof Error ? checkoutError.message : "Checkout failed",
      });
      setError(checkoutError instanceof Error ? checkoutError.message : "Checkout could not be started.");
    } finally {
      setIsLoading(false);
    }
  };

  const goBack = () => {
    setError("");
    setStep((current) => Math.max(LANDING_STEP, current - 1));
  };

  const selectedLivePrice = priceForOption(selectedPlanData);
  const selectedAnnualPrice = annualMoney(selectedLivePrice, `${selectedPlanData.price}/year`);
  const selectedPlanPrice =
    selectedIsAnnualTrial
      ? monthlyMoney(selectedLivePrice, selectedPlanData.price)
      : selectedLivePrice
        ? money(selectedLivePrice.unitAmount, selectedLivePrice.currency)
        : selectedPlanData.price;
  const selectedPlanPriceDetail =
    selectedIsAnnualTrial
      ? `${selectedPlanPrice}${selectedPlanData.priceSuffix ?? ""}, ${selectedAnnualPrice} billed yearly after trial`
      : `${selectedPlanPrice} one-time`;
  const trialPrices = pricesByOffer["polar_trial_7d"];
  const siteLifetimePrices = pricesByOffer["site_lifetime"];
  const standardMonthlyPrice = monthlyMoney(trialPrices?.standard, "A$19");
  const proMonthlyPrice = monthlyMoney(trialPrices?.pro, "A$29");
  const standardAnnualPrice = annualMoney(trialPrices?.standard, "A$19/year");
  const proAnnualPrice = annualMoney(trialPrices?.pro, "A$29/year");
  const lifetimePrice = siteLifetimePrices?.pro
    ? money(siteLifetimePrices.pro.unitAmount, siteLifetimePrices.pro.currency)
    : "A$39";
  const checkoutCtaLabel = isLoading
    ? "Redirecting…"
    : isTrialCheckoutOffer
      ? selectedPlanData.cta
      : `Checkout · ${selectedPlanPrice}`;
  const selectedTrustLine =
    selectedIsAnnualTrial
      ? `Secure checkout · ${checkoutOffer.trialDays}-day trial · ${selectedAnnualPrice} billed yearly`
      : `Secure checkout · Pay once · ${moneyBackGuaranteeLabel}`;
  const emailStepTitle = isTrialCheckoutOffer
    ? `Start your ${checkoutOffer.trialDays}-day free trial`
    : "Get lifetime access to AgentMax";
  const emailStepBody = isTrialCheckoutOffer
    ? "Enter your email to continue. Full access, no charge today, cancel anytime."
    : "Enter your email to continue. Pay once, own it forever, no subscription.";
  const emailStepCta = isTrialCheckoutOffer ? "Continue to my free trial" : "Continue to checkout";
  const activeEmailFrictionHandlers = isTrialCheckoutOffer
    ? emailFrictionHandlersTrial
    : emailFrictionHandlers;
  const paymentEyebrow = isTrialCheckoutOffer ? `${checkoutOffer.trialDays}-day trial + lifetime` : "One-time purchase";
  // The big idea leads the checkout on BOTH offers; the eyebrow above it already
  // says which one you're on ("7-day trial + lifetime" vs "One-time purchase").
  const paymentTitle = deepMatch?.paymentTitle ?? "All your agents. One notch.";
  // The trial funnel sells both paths, so it must not claim "pay once" in the same breath.
  const paymentBody =
    deepMatch?.paymentBody ??
    (isTrialCheckoutOffer
      ? "See Claude Code, Codex, Cursor and more on one notch. Free trial or lifetime options available."
      : "See Claude Code, Codex, Cursor and more on one notch. Pay once, local-first, lifetime updates.");
  const paymentMechanics = isTrialCheckoutOffer
    ? "No charge today. Annual billing starts only after your trial, or choose Pro lifetime."
    : "Pay once. Lifetime updates included.";
  const activeTestimonials = isTrialCheckoutOffer
    ? testimonials.filter((testimonial) => !testimonial.quote.toLowerCase().includes("no subscription"))
    : testimonials;
  const featuredTestimonials = activeTestimonials.slice(0, 3);
  const basePaymentFaqs = isTrialCheckoutOffer
    ? [
        {
          question: "What happens after the free trial?",
          answer: `Standard and Pro are free for ${checkoutOffer.trialDays} days, then billed yearly unless canceled. Standard is ${standardMonthlyPrice}/month, ${standardAnnualPrice} billed yearly. Pro is ${proMonthlyPrice}/month, ${proAnnualPrice} billed yearly. Lifetime is ${lifetimePrice} paid once.`,
        },
        ...paymentFaqs.filter(
          (faq) =>
            faq.question !== "Is this a subscription?" &&
            !faq.question.toLowerCase().includes("one-time") &&
            !faq.question.toLowerCase().includes("one time"),
        ),
      ]
    : paymentFaqs;
  // Loop runs in the cloud, so drop the "your work stays on your Mac" FAQ that would contradict it.
  const activePaymentFaqs = cloudConcept
    ? basePaymentFaqs.filter((faq) => !faq.question.toLowerCase().includes("sent to the cloud"))
    : basePaymentFaqs;
  const checkoutTimelineSteps: readonly CheckoutTimelineStep[] = isTrialCheckoutOffer && selectedIsAnnualTrial
    ? [
        {
          title: "Start your free trial",
          subtitle: "Use the same email for checkout and activation.",
        },
        {
          title: "Download instantly and activate",
          subtitle: "Activate with the inbox you used at checkout.",
        },
        {
          title: "Get a reminder before renewal",
          subtitle: "We'll email you 3 days before the trial ends.",
          icon: "bell" as const,
        },
        {
          title: "Keep using AgentMax after the trial",
          subtitle: checkoutOffer.renewalCopy,
        },
      ]
    : checkoutAfterPaymentSteps;

  return (
    <div
      className={`page-shell page-shell--funnel ${showWelcome || isLandingScreen ? "page-shell--welcome" : ""} ${showEmail ? "page-shell--email" : ""} ${showPayment ? "page-shell--payment" : ""}`}
    >
      <header className={`funnel-header ${showWelcome || isLandingScreen ? "funnel-header--welcome" : ""}`}>
        {canGoBack && (
          <button className="top-back" type="button" onClick={goBack} aria-label="Go back">
            ←
          </button>
        )}
        <a className="brand-lockup" href="https://agentmax.dev" aria-label="AgentMax home">
          <PixelMark className="brand-lockup__icon" size={30} />
          <span className="brand-lockup__wordmark">AgentMax</span>
        </a>
        {!showPayment && (
          <div className="progress-track" aria-hidden="true">
            <span style={{ width: `${progress}%` }} />
          </div>
        )}
      </header>

      <main className={`funnel-main ${showPayment ? "funnel-main--payment" : ""}`}>
        <section className={`funnel-card reveal ${showPayment ? "" : "funnel-card--compact"}`}>
          {showWelcome ? (
            <div className="hero-step">
              <div className="hero-step__layout">
                <div className="hero-step__copy">
                  <div className="hero-chip">
                    <PixelMark className="hero-chip__icon" size={18} />
                    Built for macOS
                  </div>
                  <h1>
                    <span className="hero-title-main">{entryConcept.heroTitleMain}</span>
                    {entryConcept.heroTitleAccent ? (
                      <span className="hero-title-accent">{entryConcept.heroTitleAccent}</span>
                    ) : null}
                  </h1>
                  <p className="hero-body">{entryConcept.heroSubtitle}</p>
                </div>
                <div className="hero-step__visual hero-visual--float" aria-hidden="true">
                  <HeroVisual variant={deepMatch?.heroVisual} agentsUsed={answers.agents_used} />
                </div>
                <div className="hero-step__actions">
                  <div className="hero-actions">
                    <button
                      className="button button-primary button-hero"
                      type="button"
                      onClick={advanceFromWelcome}
                    >
                      Get started
                    </button>
                  </div>
                  <p className="welcome-cta-friction">
                    {cloudConcept
                      ? isTrialCheckoutOffer
                        ? `${checkoutOffer.trialDays}-day trial · Runs overnight · Cancel anytime`
                        : "One-time purchase · Runs overnight · No subscription"
                      : isTrialCheckoutOffer
                        ? `${checkoutOffer.trialDays}-day trial · Local-first · Cancel anytime`
                        : "One-time purchase · Local-first · No subscription"}
                  </p>
                  <TrustBadge className="trust-badge--hero" />
                  <div className="trust-row">
                    <span className="trust-row__chip">
                      <Icon name={cloudConcept ? "clock" : "shield"} />
                      {cloudConcept ? "Runs overnight" : "Local first"}
                    </span>
                    <span className="trust-row__chip">
                      <Icon name={cloudConcept ? "check" : "battery"} />
                      {cloudConcept ? "No babysitting" : "Battery aware"}
                    </span>
                    <span className="trust-row__chip trust-row__chip--agents">
                      <span className="agent-logos" aria-hidden="true">
                        <BrandLogo name="claude" />
                        <BrandLogo name="openai" />
                        <BrandLogo name="cursor" />
                      </span>
                      Built for agents
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          {showQuestion && currentQuestion ? (
            <div className={`question-step ${isLandingScreen ? "question-step--landing" : ""}`}>
              {isLandingScreen ? (
                <div className="hero-chip">
                  <PixelMark className="hero-chip__icon" size={18} />
                  Built for macOS
                </div>
              ) : null}
              <h2>{currentQuestion.title}</h2>
              <p className="hero-body">{currentQuestion.subtitle}</p>
              <div className={`option-grid ${currentQuestion.compact || isLandingScreen ? "option-grid--compact" : ""}`}>
                {currentQuestion.options.map((option) => {
                  const selectedIds = (answers[currentQuestion.id] ?? "").split(",").filter(Boolean);
                  const isSelected = selectedIds.includes(option.id);
                  return (
                    <button
                      className={`option-card tone-${option.tone} ${currentQuestion.compact || isLandingScreen ? "option-card--compact" : ""} ${isSelected ? "is-selected" : ""}`}
                      type="button"
                      key={option.id}
                      aria-pressed={currentQuestion.multiSelect ? isSelected : undefined}
                      onClick={() =>
                        currentQuestion.multiSelect
                          ? toggleAnswer(currentQuestion.id, option.id)
                          : selectAnswer(currentQuestion.id, option.id)
                      }
                    >
                      <span className={`option-dot ${option.brand ? `option-dot--${option.brand}` : ""}`}>
                        {option.brand ? <BrandLogo name={option.brand} /> : <QuestionIcon name={option.icon} />}
                      </span>
                      <strong>{option.label}</strong>
                      {currentQuestion.compact || isLandingScreen ? null : <small>{option.description}</small>}
                    </button>
                  );
                })}
              </div>
              {currentQuestion.multiSelect ? (
                <button
                  className="button button-primary button-hero question-continue"
                  type="button"
                  disabled={!(answers[currentQuestion.id] ?? "").split(",").filter(Boolean).length}
                  onClick={advanceFromQuestion}
                >
                  Continue
                </button>
              ) : null}
              {step === LANDING_STEP ? <TrustBadge className="trust-badge--question" /> : null}
            </div>
          ) : null}

          {showResult ? (
            <div className="result-step">
              <div className="result-hero result-hero--card">
                <div className="result-hero__copy">
                  <span className="result-eyebrow">{deepMatch ? deepMatch.thread : "Here's what your fleet looks like"}</span>
                  <div className="result-metric">
                    <strong className="result-metric__value">~{resultInsights.exposedHoursPerWeek}</strong>
                    <span className="result-metric__unit">hrs/week</span>
                  </div>
                  <p className="result-metric__label">
                    {(() => {
                      const label = deepMatch
                        ? deepMatch.resultMetricLabel
                        : "of agents working on your Mac, with no good way to see what they're all doing";
                      const [before, ...rest] = label.split("agents");
                      return (
                        <>
                          {before}
                          <strong>{listPickedAgents(answers.agents_used)}</strong>
                          {rest.join("agents")}
                        </>
                      );
                    })()}
                  </p>
                </div>
                <div className="result-hero__companion" aria-hidden="true">
                  <img src="/assets/brand/robot-hover-v7-256.webp" alt="" />
                  <div className="result-hero__proof">
                    <span><i /> Live on your Mac</span>
                    <strong>One calm view.<br />No terminal roulette.</strong>
                  </div>
                </div>
              </div>

              {/* Two editorial columns, split by a rule: their setup vs what AgentMax does. */}
              <div className="result-split">
                <div className="result-col result-col--problem">
                  <div className="result-col__heading">
                    <p className="result-col__head">Your setup today</p>
                    <span className="result-col__status">Friction detected</span>
                  </div>
                  <ul className="result-pains" aria-label="The problem">
                    <li className={deepMatch ? "is-picked" : undefined}>
                      <span className="result-pains__pixel" aria-hidden="true" />
                      {deepMatch ? deepMatch.resultProblems[0] : "no proper way to see all your agents"}
                      <span className="result-pains__logos">
                        {getPickedAgentChips(answers.agents_used).map((agent) => (
                          <span className="result-pains__agent" key={agent.brand}>
                            <BrandLogo name={agent.brand} />
                            {agent.label}
                          </span>
                        ))}
                      </span>
                    </li>
                    <li className={deepMatch ? undefined : "is-picked"}>
                      <span className="result-pains__pixel" aria-hidden="true" />
                      {deepMatch ? deepMatch.resultProblems[1] : resultInsights.riskShort}
                    </li>
                    <li>
                      <span className="result-pains__pixel" aria-hidden="true" />
                      all on {resultInsights.macSetup.toLowerCase()}
                    </li>
                  </ul>
                </div>
                <div className="result-col result-col--solution">
                  <p className="result-col__head">What AgentMax does</p>
                  <ul className="result-fixes">
                    {resultFeatureOverview.map((feature) => (
                      <li key={feature.title}>
                        <span className={`result-feature__icon result-feature__icon--${feature.icon}`} aria-hidden="true">
                          {feature.icon === "terminal" ? (
                            <span className="result-feature__traffic-lights">
                              <i className="is-red" />
                              <i className="is-amber" />
                              <i className="is-green" />
                            </span>
                          ) : (
                            <QuestionIcon name={feature.icon} />
                          )}
                        </span>
                        {feature.title}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* The one strong object: recommended plan. */}
              <div className="result-panel">
                <div className="result-panel__copy">
                  <span className="result-panel__badge">Recommended</span>
                  <h3>
                    {resultInsights.planName} · {resultInsights.planDetail}
                  </h3>
                  <p className="hero-body">
                    {recommendedPlan === "standard"
                      ? isTrialCheckoutOffer
                        ? "Try AgentMax on one Mac with battery guardrails built in."
                        : "One Mac with Agent Island, close-lid continuity, and battery guardrails built in."
                      : isTrialCheckoutOffer
                        ? "Try AgentMax across work and personal machines before the annual plan begins."
                        : "Best value for developers running agents across work and personal machines."}
                  </p>
                  <ul className="result-bullets">
                    {resultBullets.map((bullet) => (
                      <li key={bullet}>
                        <Icon name="check" />
                        {bullet}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="result-panel__visual">
                  <FunnelDemo image={deepMatch?.productImage} />
                  <img className="result-panel__robot" src="/assets/brand/robot-hover-v7-256.webp" alt="" aria-hidden="true" />
                </div>
                <div className="result-panel__actions">
                  <button className="button button-primary button-hero" type="button" onClick={advanceFromResult}>
                    Continue
                  </button>
                  <TrustBadge className="trust-badge--result" />
                </div>
              </div>
              <FunnelVideoTestimonials />
            </div>
          ) : null}

          {showEmail ? (
            <div className="email-step">
              <div className="section-intro section-intro--narrow">
                <p className="section-kicker">Almost there</p>
                <h2>{emailStepTitle}</h2>
                <p className="hero-body">{emailStepBody}</p>
              </div>
              <label className="email-field">
                <span className="email-field__label">Email for download and activation</span>
                <input
                  type="email"
                  value={email}
                  placeholder="you@example.com"
                  autoComplete="email"
                  onChange={(event) => setEmail(event.target.value)}
                />
              </label>
              {error ? <p className="error-text">{error}</p> : null}
              <button className="button button-primary button-hero" type="button" disabled={isLoading} onClick={saveEmailAndContinue}>
                {isLoading ? "Saving…" : emailStepCta}
              </button>
              <div className="email-friction-handlers" aria-label="Email reassurance">
                {activeEmailFrictionHandlers.map((item) => (
                  <span key={item}>
                    <Icon name="check" />
                    {item}
                  </span>
                ))}
              </div>
              <TrustBadge className="trust-badge--email" />
              <p className="welcome-cta-friction">
                By continuing you agree to AgentMax&apos;s{" "}
                <a href={brand.termsUrl} target="_blank" rel="noreferrer">
                  Terms
                </a>{" "}
                and{" "}
                <a href={brand.privacyPolicyUrl} target="_blank" rel="noreferrer">
                  Privacy Policy
                </a>
                .
              </p>
            </div>
          ) : null}

          {showPayment ? (
            <div className="payment-step" id="pricing">
              <div className="payment-step__hero">
                <div className="section-intro section-intro--narrow">
                  <p className="section-kicker">{paymentEyebrow}</p>
                  <h2>{paymentTitle}</h2>
                  <p className="hero-body">{paymentBody}</p>
                </div>
                <div className="payment-step__visual">
                  <FunnelDemo image={deepMatch?.productImage} />
                </div>
                {/* Sits after the demo in DOM order so it reads as a footnote to the
                    product shot, not a banner between the copy and the visual. */}
                <p className="payment-step__mechanics">
                  <Icon name="check" />
                  {paymentMechanics}
                </p>
              </div>
              <div className={`plan-grid ${isTrialCheckoutOffer ? "plan-grid--three" : ""}`} role="radiogroup" aria-label="Choose your AgentMax plan">
                {activeCheckoutPlans.map((plan) => {
                  const livePrice = priceForOption(plan);
                  const annualPrice = annualMoney(livePrice, `${plan.price}/year`);
                  const displayPrice =
                    plan.billingMode === "annual_trial"
                      ? monthlyMoney(livePrice, plan.price)
                      : livePrice ? money(livePrice.unitAmount, livePrice.currency) : plan.price;
                  const priceTerm = plan.billingMode === "annual_trial" ? (plan.priceSuffix ?? "/month") : "one-time";
                  const planFeatures = plan.billingMode === "annual_trial"
                    ? [
                        `${checkoutOffer.trialDays} days free`,
                        `${annualPrice} billed yearly`,
                        plan.planId === "standard" ? "1 Mac" : "Up to 5 Macs",
                        "Cancel anytime",
                      ]
                    : plan.features.slice(0, 4);
                  const isCollapsed = isMobile && expandedMobileOptionId !== plan.id;
                  const isFeatured = plan.badgeVariant === "primary" || plan.id === "pro";
                  return (
                    <article
                      className={`plan-card ${selectedOptionId === plan.id ? "is-selected" : ""} ${isFeatured ? "is-featured" : ""} ${plan.billingMode === "annual_trial" ? "is-trial" : ""} ${isCollapsed ? "plan-card--mobile-collapsed" : ""}`}
                      key={plan.id}
                      role="radio"
                      aria-checked={selectedOptionId === plan.id}
                      tabIndex={0}
                      onClick={() => {
                        setSelectedOptionId(plan.id);
                        setExpandedMobileOptionId(plan.id);
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          setSelectedOptionId(plan.id);
                          setExpandedMobileOptionId(plan.id);
                        }
                      }}
                    >
                      {plan.badge ? <span className="plan-badge">{plan.badge}</span> : null}
                      <span className="plan-card__chevron" aria-hidden="true">›</span>
                      <p className="plan-usage">{plan.detail}</p>
                      <h3>{plan.name}</h3>
                      <div className="plan-price">
                        {isTrialCheckoutOffer ? null : <span className="plan-price__original">{plan.originalPrice}</span>}
                        <span className="plan-price__main">
                          <strong>{displayPrice}</strong>
                          <span className="plan-price__term">{priceTerm}</span>
                        </span>
                      </div>
                      <p className="plan-description">
                        {plan.billingMode === "annual_trial"
                          ? `${checkoutOffer.trialDays} days free, then ${annualPrice} billed yearly unless canceled.`
                          : plan.description}
                      </p>
                      <ul>
                        {planFeatures.map((f) => (
                          <li key={f}>
                            <Icon name="check" />
                            {f}
                          </li>
                        ))}
                      </ul>
                    </article>
                  );
                })}
              </div>
              {error ? <p className="error-text">{error}</p> : null}
              <div className="checkout-trust-panel">
              <TrustBadge />
              <div className="pricing-trust">
                <span>
                  <Icon name="lock" />
                  Secure checkout
                </span>
                <span>
                  <Icon name="download" />
                  Instant download after checkout
                </span>
                <span>
                  <Icon name="shield" />
                  {selectedIsAnnualTrial ? "Cancel anytime" : moneyBackGuaranteeLabel}
                </span>
              </div>
              </div>
              <section className="checkout-next-steps" aria-label="What happens after payment">
                <div className="checkout-next-steps__copy">
                  <p className="section-kicker">After payment</p>
                  <h3>{selectedIsAnnualTrial ? "After trial checkout" : "The next 30 seconds are simple"}</h3>
                  <img className="checkout-next-steps__robot" src="/assets/brand/robot-hover-v7-256.webp" alt="" aria-hidden="true" />
                </div>
                <ol className="checkout-timeline">
                  {checkoutTimelineSteps.map((item, index) => (
                    <li key={item.title}>
                      <span className={item.icon ? "checkout-timeline__index checkout-timeline__index--icon" : "checkout-timeline__index"}>
                        {item.icon ? <Icon name={item.icon} /> : index + 1}
                      </span>
                      <p>
                        <strong>{item.title}</strong>
                        <span>{item.subtitle}</span>
                      </p>
                    </li>
                  ))}
                </ol>
              </section>
              <section className="payment-testimonials" aria-label="Customer reviews">
                <div className="payment-rating-proof">
                  <span className="payment-rating-proof__stars" aria-hidden="true">
                    ★★★★★
                  </span>
                  <p>
                    Rated {socialProof.rating}/{socialProof.ratingMax} by {socialProof.audience}
                  </p>
                </div>
                <div className="review-marquee">
                  <div className="review-marquee__track">
                    {featuredTestimonials.map((t) => (
                      <article className="review-card" key={t.author}>
                        <span className="review-card__stars" aria-hidden="true">
                          ★★★★★
                        </span>
                        <blockquote>&ldquo;{t.quote}&rdquo;</blockquote>
                        <div className="review-meta">
                          <img className="review-avatar" src={t.image} alt="" width={28} height={28} loading="lazy" />
                          <span className="review-meta__copy">
                            <strong>{t.author}</strong>
                            <span>{t.detail}</span>
                          </span>
                        </div>
                      </article>
                    ))}
                  </div>
                </div>
              </section>
              <section className="product-highlight product-highlight--open">
                <div className="product-highlight__head">
                  <span className="section-kicker">How AgentMax works</span>
                  <strong>Four capabilities. One calm notch.</strong>
                </div>
                <div className="product-highlight__content">
                  <div className="payment-explainer" aria-label="How AgentMax works step by step">
                    {(() => {
                      const featuredIndex = deepMatch?.featuredExplainerIndex ?? getFeaturedExplainerIndex(answers);
                      // Personalize the emphasis, not the feature set: checkout always
                      // explains the same four shipped AgentMax capabilities.
                      const featured = paymentExplainerSteps[featuredIndex] ?? paymentExplainerSteps[0];
                      const rest = paymentExplainerSteps.filter((_, index) => index !== featuredIndex);
                      return (
                        <>
                          <article className="payment-explainer-card payment-explainer-card--key shimmer-frame">
                            <span className="payment-explainer-card__tag">Built for you</span>
                            <strong>{featured.title}</strong>
                            <p>{featured.description}</p>
                            <span className="payment-explainer-card__agents" aria-hidden="true">
                              <BrandLogo name="claude" />
                              <BrandLogo name="openai" />
                              <BrandLogo name="cursor" />
                            </span>
                          </article>
                          {rest.map((stepItem) => (
                            <article key={stepItem.title} className={`payment-explainer-card tone-${stepItem.tone}`}>
                              <span className="option-dot" aria-hidden="true">
                                <QuestionIcon name={stepItem.icon} />
                              </span>
                              <strong>{stepItem.title}</strong>
                              <p>{stepItem.description}</p>
                            </article>
                          ))}
                        </>
                      );
                    })()}
                  </div>
                </div>
              </section>
              <section className="faq-block" aria-label="Frequently asked questions">
                <p className="section-kicker">FAQ</p>
                <h3 className="faq-block__title">Questions before checkout?</h3>
                <div className="faq-list">
                  {activePaymentFaqs.map((faq) => (
                    <details className="faq-item" key={faq.question}>
                      <summary>
                        <span>{faq.question}</span>
                        <Icon name="chevron" className="faq-item__chevron" />
                      </summary>
                      <p>
                        {faq.answer}
                        {"href" in faq ? (
                          <>
                            {" "}
                            <a href={faq.href} target="_blank" rel="noreferrer">
                              {faq.linkLabel}
                            </a>
                          </>
                        ) : null}
                      </p>
                    </details>
                  ))}
                </div>
              </section>
            </div>
          ) : null}
        </section>

        {showPayment ? (
          <aside className="side-proof" aria-label="Checkout">
            <div className="side-proof__cta">
              <div className="side-proof__heading">
                <span className="side-proof__brand-tile" aria-hidden="true">
                  <PixelMark size={24} />
                </span>
                <span className="side-proof__heading-copy">
                  <strong>Get AgentMax</strong>
                  <span>
                    {selectedPlanData.name} · {selectedPlanPriceDetail}
                  </span>
                </span>
              </div>
              {!showDedicatedEmailStep ? (
                <div className="payment-email-inline">
                  <input
                    id="checkout-email-sidebar"
                    className="payment-email-inline__input"
                    type="email"
                    value={email}
                    placeholder="you@example.com"
                    inputMode="email"
                    autoComplete="email"
                    aria-label="Email for activation details"
                    onBlur={(event) => captureEmailLead(event.target.value)}
                    onChange={(event) => setEmail(event.target.value)}
                  />
                </div>
              ) : null}
              <div
                className="checkout-card-rating"
                aria-label={`Rated ${socialProof.rating} out of ${socialProof.ratingMax}, trusted by ${socialProof.audience}`}
              >
                <span className="checkout-card-rating__stars" aria-hidden="true">
                  ★★★★★
                </span>
                <span>
                  {socialProof.rating}/{socialProof.ratingMax} · {socialProof.audience}
                </span>
              </div>
              <button
                className="button button-primary checkout-cta-button"
                type="button"
                disabled={isLoading}
                onClick={() => startCheckout(selectedOptionId)}
              >
                {checkoutCtaLabel}
              </button>
              <p className="side-proof__cta-note">
                {isTrialCheckoutOffer ? selectedTrustLine : "Secure checkout · Pay once · Use forever"}
              </p>
              <p className="checkout-guarantee">
                <Icon name="shield" />
                <span>
                  <strong>{isTrialCheckoutOffer ? "Cancel anytime" : moneyBackGuaranteeLabel}</strong>
                  <small>{isTrialCheckoutOffer ? checkoutOffer.renewalCopy : "If AgentMax isn't a fit."}</small>
                </span>
              </p>
            </div>
            <div className="proof-metrics">
              <span>
                <Icon name={cloudConcept ? "clock" : "shield"} />
                <strong>{cloudConcept ? "Runs overnight" : "Local-first"}</strong>
              </span>
              <span>
                <Icon name="lock" />
                <strong>Secure checkout</strong>
              </span>
              <span>
                <Icon name="download" />
                <strong>Instant access</strong>
              </span>
            </div>
          </aside>
        ) : null}
      </main>

      {showPayment && showFloatingCheckoutCta ? (
        <div className="floating-checkout-cta" role="region" aria-label="Checkout">
          {!showDedicatedEmailStep ? (
            <div className="payment-email-inline">
              <input
                id="checkout-email-floating"
                className="payment-email-inline__input"
                type="email"
                value={email}
                placeholder="you@example.com"
                inputMode="email"
                autoComplete="email"
                aria-label="Email for activation details"
                onBlur={(event) => captureEmailLead(event.target.value)}
                onChange={(event) => setEmail(event.target.value)}
              />
            </div>
          ) : null}
          <button
            className="button button-primary checkout-cta-button"
            type="button"
            disabled={isLoading}
            onClick={() => startCheckout(selectedOptionId)}
          >
            {checkoutCtaLabel}
          </button>
          <p className="floating-checkout-cta__trust">
            {isTrialCheckoutOffer ? selectedTrustLine : "Pay once · 15-day guarantee · Secure checkout"}
          </p>
        </div>
      ) : null}

      <footer className="funnel-footer">
        <span className="funnel-footer__brand">
          <span className="funnel-footer__mark" aria-hidden="true"><PixelMark size={16} /></span>
          AgentMax for macOS
        </span>
        <a href={brand.privacyPolicyUrl} target="_blank" rel="noreferrer">
          Privacy
        </a>
        <a href={brand.termsUrl} target="_blank" rel="noreferrer">
          Terms
        </a>
      </footer>
    </div>
  );
}
